import { IconPhoto } from "@tabler/icons-preact";
import { useState } from "preact/hooks";
import type { EntityIcon, IconColor, IconName } from "../../api/types.gen.ts";
import { type Key, locale, t } from "../../i18n/index.ts";
import { type Catalog, catalog, loadCatalog } from "../../lib/iconCatalog.ts";
import { loadKeywords, matches } from "../../lib/iconSearch.ts";
import { ignore } from "../../lib/ignore.ts";
import { Button, Tile } from "../../ui/Button.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { EntityGlyph } from "../../ui/EntityGlyph.tsx";
import { Input } from "../../ui/form.tsx";
import { ICON_SWATCH } from "../../ui/iconColors.ts";
import { FOCUS } from "../../ui/styles.ts";

const COLORS = Object.keys(ICON_SWATCH) as IconColor[];
const BIG = 28;

/** A row of color swatches, plus a first "automatic" / "none" choice (null). */
function Swatches(p: {
  label: string;
  empty: string;
  value: IconColor | undefined;
  onPick: (c: IconColor | null) => void;
}) {
  const ring = "ring-2 ring-zinc-900 ring-offset-2";
  return (
    <fieldset class="flex flex-wrap items-center gap-2">
      <legend class="mb-2 text-sm font-medium text-zinc-700">{p.label}</legend>
      <button
        type="button"
        aria-pressed={p.value === undefined}
        onClick={() => p.onPick(null)}
        class={`rounded-full border border-zinc-300 px-2 py-0.5 text-xs text-zinc-600 ${FOCUS} ${p.value === undefined ? ring : ""}`}
      >
        {p.empty}
      </button>
      {COLORS.map((c) => (
        <button
          key={c}
          type="button"
          aria-label={t(`icons.color.${c}`)}
          title={t(`icons.color.${c}`)}
          aria-pressed={p.value === c}
          onClick={() => p.onPick(c)}
          class={`size-6 rounded-full ${ICON_SWATCH[c]} ${FOCUS} ${p.value === c ? ring : ""}`}
        />
      ))}
    </fieldset>
  );
}

/** The icons, by domain, matching `q` (by name). */
function Grid(p: { icons: Catalog; q: string; value: IconName | undefined; onPick: (name: IconName) => void }) {
  const { ENTITY_ICONS, ICON_GROUPS } = p.icons;
  const lang = locale.value;
  const groups = ICON_GROUPS.map(([g, names]) => [g, names.filter((n) => matches(n, p.q, lang))] as const).filter(
    ([, names]) => names.length > 0,
  );
  if (groups.length === 0) {
    return <p class="py-6 text-center text-sm text-zinc-500">{t("icons.noMatch")}</p>;
  }
  return (
    <div class="max-h-80 overflow-y-auto pr-1">
      {groups.map(([g, names]) => (
        <section key={g} class="mb-3">
          <h3 class="mb-1 text-xs font-semibold tracking-wide text-zinc-500 uppercase">
            {t(`icons.group.${g}` as Key)}
          </h3>
          <div class="grid grid-cols-[repeat(auto-fill,minmax(3rem,1fr))] gap-0.5">
            {names.map((n) => {
              const Glyph = ENTITY_ICONS[n];
              return (
                <Tile
                  key={n}
                  label={n.replaceAll("-", " ")}
                  pressed={p.value === n}
                  onClick={() => p.onPick(n)}
                  class="h-12"
                >
                  <Glyph size={26} stroke={1.5} aria-hidden="true" />
                </Tile>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/** The icon as it will look (or a placeholder), large. */
function Preview({ icon }: { icon: EntityIcon | null }) {
  return icon ? (
    <EntityGlyph icon={icon} size={BIG} pad={6} />
  ) : (
    <IconPhoto size={BIG} stroke={1.5} aria-hidden="true" class="text-zinc-300" />
  );
}

/**
 * An entity's icon: a button showing it (or a placeholder), opening a picker with the curated icons by domain, a
 * search, and colors for the icon and its background.
 */
export function IconPicker(p: {
  value: EntityIcon | null;
  onChange: (icon: EntityIcon | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        disabled={p.disabled}
        onClick={() => {
          loadCatalog().catch(ignore);
          loadKeywords().catch(ignore);
          setOpen(true);
        }}
        aria-label={t("icons.choose")}
        title={t("icons.choose")}
        class={`flex size-14 shrink-0 items-center justify-center rounded-xl border border-zinc-300 bg-white shadow-xs hover:bg-zinc-50 disabled:opacity-60 ${FOCUS}`}
      >
        <Preview icon={p.value} />
      </button>
      <Dialog open={open} onClose={() => setOpen(false)} title={t("icons.title")}>
        <Picker value={p.value} onChange={p.onChange} onClose={() => setOpen(false)} />
      </Dialog>
    </>
  );
}

/** The picker itself: the icon as it'll look, a search, the icons, the colors; each choice made at once. */
function Picker(p: { value: EntityIcon | null; onChange: (icon: EntityIcon | null) => void; onClose: () => void }) {
  const [q, setQ] = useState("");
  const icon = p.value;
  const set = (patch: Partial<EntityIcon>) => {
    const next = { ...(icon ?? { name: "box" as IconName }), ...patch };
    p.onChange(next);
  };
  const color = (key: "color" | "background") => (c: IconColor | null) => {
    if (!icon) {
      return;
    }
    const { [key]: _drop, ...rest } = icon;
    p.onChange(c ? { ...rest, [key]: c } : rest);
  };
  return (
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-3">
        <span class="flex size-14 items-center justify-center rounded-xl border border-zinc-200">
          <Preview icon={icon} />
        </span>
        <div class="flex-1">
          <Input
            type="search"
            aria-label={t("icons.search")}
            placeholder={t("icons.search")}
            value={q}
            onValue={setQ}
          />
        </div>
      </div>
      {catalog.value ? <Grid icons={catalog.value} q={q} value={icon?.name} onPick={(name) => set({ name })} /> : null}
      {icon ? (
        <>
          <Swatches
            label={t("icons.iconColor")}
            empty={t("icons.auto")}
            value={icon.color ?? undefined}
            onPick={color("color")}
          />
          <Swatches
            label={t("icons.background")}
            empty={t("icons.none")}
            value={icon.background ?? undefined}
            onPick={color("background")}
          />
        </>
      ) : null}
      <DialogActions>
        {icon ? (
          <Button
            variant="ghost"
            onClick={() => {
              p.onChange(null);
              p.onClose();
            }}
          >
            {t("icons.remove")}
          </Button>
        ) : null}
        <Button answer="confirm" variant="primary" onClick={p.onClose}>
          {t("icons.done")}
        </Button>
      </DialogActions>
    </div>
  );
}
